import Table from '../../components/Table';
import { ordersTableHeading } from '../../utils/constants';
import { Button, useEthersAdapter, useSwitchToConfiguredChain } from '@airdao/ui-library';
import { MarketContextType } from '../../contexts/MarketsContext/provider.interface';
import useMarkets from '../../contexts/MarketsContext/useMarkets';
import React, { useEffect, useMemo, useState } from 'react';
import { collapseString } from '../../utils/collapseString';
import { getTimeRemaining } from '../../utils/getTimeRemaining';
import { getTokenByAddress } from '../../utils/getTokenByAddress';
import { formatDecimals } from '../../utils/formatDecimals';
import { getFromLocalStorage } from '../../utils/getFromLocalStorage';
import { claimBond, getExpiryClaimed } from './orders.services';
import useBondContracts from '../../contexts/BondContractContext/useBondContracts';
// @ts-ignore
import { ContractNames, Methods } from '@airdao/airdao-bond';
import { BigNumber, utils } from 'ethers';

import { useAccount } from 'wagmi';
// @ts-ignore
let disableOrders = getFromLocalStorage('savedHashes');

const Orders = () => {
  const { txs, tokens, isLoading }: MarketContextType = useMarkets();
  const contracts: any = useBondContracts();
  const { address: account, chainId } = useAccount();
  const switchToAmb = useSwitchToConfiguredChain();
  const { provider } = useEthersAdapter();
  const [totalClaimable, setTotalClaimable] = useState(0)
  const [totalBonded, setTotalBonded] = useState(0);
  const [isClaimLoading, setIsClaimLoading] = useState(false);
  const [expiryClaimed, setExpiryClaimed] = useState(null);
  const [claimedOrders, setClaimedOrders] = useState<any>({});

  useEffect(() => {
    if (account) {
      getExpiryClaimed(account)
        .then((res) => setExpiryClaimed(res.data));
    }
  }, [account]);

  const handleClaim = async (contractName: string, id: string, payout: string, hash: string, expiry: number, payoutTokenAddress: string, version: string) => {
    if (chainId?.toString() === process.env.REACT_APP_CHAIN_ID) {
      let idOrAddress = id;
      if (version === 'v1') {
        idOrAddress = contractName === ContractNames.FixedExpiryTeller
          ? await Methods.bondFixedExpiryTellerGetBondToken(contracts, contractName, payoutTokenAddress, expiry)
          : id;
      }

      setIsClaimLoading(true);
      disableOrders = getFromLocalStorage('savedHashes');
      disableOrders[hash] = true;
      localStorage.setItem('savedHashes', JSON.stringify(disableOrders));
      await claimBond(contracts, contractName, idOrAddress, payout, hash, account || '', deleteLoading, version, provider, handleClaimedOrders)
    }
  }

  const handleClaimedOrders = (hash: string) => {
    setClaimedOrders((state: any) => ({...state, [hash]: true}))
  };

  const deleteLoading = (hash: string) => {
    setIsClaimLoading(false);
    const savedHashesNew = getFromLocalStorage('savedHashes');
    delete savedHashesNew[hash];
    localStorage.setItem('savedHashes', JSON.stringify(savedHashesNew));
  }

  const ordersTableData = useMemo(() => {
    setTotalClaimable(0);
    setTotalBonded(0);
    if (!expiryClaimed) return [];

    return txs.sort((a, b) => b.date - a.date).map((el) => {
      const payoutToken = getTokenByAddress(el.payoutToken, tokens);
      const quoteToken = getTokenByAddress(el.quoteToken, tokens);

      const vestingEndsDate:number = el.vestingType === 'Fixed-expiry' ? +el.vesting : (+el.vesting + el.date);
      const isVestingPass:boolean = vestingEndsDate * 1000 < new Date().getTime();

      const amountBn = BigNumber.from(el.bondAmount);
      const payoutBn = BigNumber.from(el.payoutAmount);

      const amount = utils.formatUnits(amountBn, quoteToken?.decimals);
      const payout = utils.formatUnits(payoutBn, payoutToken?.decimals);

      const amountPrice = +amount * (quoteToken?.price || 0);
      const payoutPrice = +payout * (payoutToken?.price || 0);

      const contractName = el.vestingType === 'Fixed-expiry' ? ContractNames.FixedExpiryTeller : ContractNames.FixedTermTeller;
      if (!el.isClaimed) {
        setTotalBonded((state) => state + payoutPrice);
      }
      if (isVestingPass && !el.isClaimed) {
        setTotalClaimable((state) => state + payoutPrice)
      }
      const claim = async () => {
        switchToAmb();
        handleClaim(
          contractName,
          el.eventId,
          el.payoutAmount,
          el.txHash,
          el.vesting,
          payoutToken?.contractAddress || '',
          el.version,
        )
      }
      const actionButton = el.isClaimed ? (
        <span className="green-label">Claimed</span>
      ) : (
        <Button
          type="primary"
          size="small"
          onClick={claim}
          disabled={isClaimLoading || !isVestingPass || !!claimedOrders[el.txHash]}
        >
          Claim
        </Button>
      );

      return [
        <span className="table__row-icon">
          <img src={payoutToken?.logoUrl} alt=""/>
          {payoutToken?.symbol.toUpperCase()}
        </span>,
        <span className="table__row-icon">
          <img src={quoteToken?.logoUrl} alt=""/>
          {quoteToken?.symbol.toUpperCase()}
        </span>,
        <div>
          <p>{formatDecimals(amount, quoteToken?.contractAddress, tokens)} {quoteToken?.symbol.toUpperCase()}</p>
          <p className="table-price">${amountPrice.toFixed(2)}</p>
        </div>,
        <div>
          <p>{formatDecimals(payout, payoutToken?.contractAddress, tokens)} {payoutToken?.symbol.toUpperCase()}</p>
          <p className="table-price">${payoutPrice.toFixed(2)}</p>
        </div>,
        <span className={isVestingPass ? 'color-grey' : ''}>
          {getTimeRemaining(vestingEndsDate * 1000)}
        </span>,
        <a href={`https://airdao.io/explorer/tx/${el.txHash}/`} className="link" target="_blank">
          {collapseString(el.txHash)}
        </a>,
        actionButton,
      ];
    });
  }, [txs, isClaimLoading, account, expiryClaimed]);

  const loading = isLoading.txs || isLoading.tokens;

  return (
    <div className="orders">
      <h1 className="title orders-title">My Orders</h1>
      <div className="orders__info">
        <div>
          <p className="orders__info-label">TOTAL VALUE BONDED</p>
          <p className="orders__info-value">${loading ? 0 : totalBonded.toFixed(2)}</p>
        </div>
        <div>
          <p className="orders__info-label">CLAIMABLE</p>
          <p className="orders__info-value">${loading ? 0 : totalClaimable.toFixed(2)}</p>
        </div>
      </div>
      <Table
        headingCells={ordersTableHeading}
        rowCells={ordersTableData}
        gridTemplateClassName="orders__grid-template"
        isLoading={loading}
      />
    </div>
  );
};

export default Orders;
